/**
 * Portal Voucher Scanner / Redemption Page
 *
 * Validate a voucher (by code or scanned/pasted QR payload), then redeem it.
 * Supports live camera scanning via html5-qrcode, manual code entry and
 * pasting the raw QR JSON payload.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { fmtParede } from '../../utils/tempoCv';
import vouchersService, {
  PortalResgateHistorico,
  VoucherValidacao,
} from '../../services/vouchers.service';

const PortalVoucherScanner: React.FC = () => {
  const [input, setInput] = useState('');
  const [voucher, setVoucher] = useState<VoucherValidacao | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Redemption form
  const [valorResgate, setValorResgate] = useState<string>('');
  const [notas, setNotas] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  // History
  const [historico, setHistorico] = useState<PortalResgateHistorico[]>([]);

  // Camera scanner
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerElemId = 'voucher-qr-reader';

  const loadHistorico = async () => {
    try {
      const data = await vouchersService.portalGetResgatesHistorico({ limit: 15 });
      setHistorico(data);
    } catch {
      /* non-blocking */
    }
  };

  useEffect(() => {
    loadHistorico();
  }, []);

  const reset = () => {
    setInput('');
    setVoucher(null);
    setValorResgate('');
    setNotas('');
    setError(null);
    setSuccessMsg(null);
  };

  const handleValidar = async (e?: React.FormEvent, rawValue?: string) => {
    e?.preventDefault();
    setError(null);
    setSuccessMsg(null);
    setVoucher(null);
    setValorResgate('');

    const trimmed = (rawValue ?? input).trim();
    if (!trimmed) {
      setError('Insere o código do voucher ou cola os dados do QR.');
      return;
    }

    // Heuristic: if it looks like JSON, send as qr_code_data, else as codigo_unico
    const payload = trimmed.startsWith('{')
      ? { qr_code_data: trimmed }
      : { codigo_unico: trimmed };

    try {
      setChecking(true);
      const data = await vouchersService.portalValidarVoucher(payload);
      setVoucher(data);
      // Pre-fill with full remaining value for "valor" vouchers
      if (data.tipo_categoria === 'valor' && data.valor_restante != null) {
        setValorResgate(String(data.valor_restante));
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao validar voucher');
    } finally {
      setChecking(false);
    }
  };

  // ──────────────── Camera scanner lifecycle ────────────────

  const stopCamera = async () => {
    const scanner = scannerRef.current;
    if (scanner) {
      try {
        if (scanner.isScanning) {
          await scanner.stop();
        }
        scanner.clear();
      } catch {
        /* ignore */
      }
      scannerRef.current = null;
    }
  };

  const startCamera = async () => {
    setCameraError(null);
    setCameraOpen(true);
    // Wait one frame so the target div mounts before init
    await new Promise((r) => setTimeout(r, 0));

    try {
      const scanner = new Html5Qrcode(scannerElemId);
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: (w, h) => {
            const side = Math.floor(Math.min(w, h) * 0.7);
            return { width: side, height: side };
          },
        },
        async (decodedText) => {
          // Prevent multiple triggers
          if (!scannerRef.current) return;
          setInput(decodedText);
          await stopCamera();
          setCameraOpen(false);
          handleValidar(undefined, decodedText);
        },
        () => {
          /* silent scan-miss callback */
        }
      );
    } catch (err: any) {
      setCameraError(
        err?.message ||
          'Não foi possível aceder à câmera. Verifica as permissões do browser.'
      );
      await stopCamera();
      setCameraOpen(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const handleResgatar = async () => {
    if (!voucher || !voucher.valido) return;
    setError(null);

    const isValor = voucher.tipo_categoria === 'valor';
    let valor: number | null = null;

    if (isValor) {
      valor = Number(valorResgate);
      if (!valor || valor <= 0) {
        setError('Valor a resgatar é obrigatório para vouchers de valor.');
        return;
      }
      if (voucher.valor_restante != null && valor > voucher.valor_restante) {
        setError(
          `Valor excede o saldo restante (${voucher.valor_restante.toLocaleString('pt-CV')} ECV).`
        );
        return;
      }
    }

    try {
      setRedeeming(true);
      const result = await vouchersService.portalResgatarVoucher({
        compra_id: voucher.id,
        valor_resgatado: isValor ? valor : null,
        desconto_aplicado: !isValor ? voucher.percentagem_desconto : null,
        notas: notas || null,
        metodo_validacao: input.trim().startsWith('{') ? 'qr_code' : 'codigo_manual',
      });

      setSuccessMsg(
        `✓ ${result.message}. ${
          result.novo_status === 'parcialmente_usado'
            ? `Saldo restante: ${result.valor_restante.toLocaleString('pt-CV')} ECV.`
            : 'Voucher totalmente utilizado.'
        }`
      );
      setVoucher(null);
      setInput('');
      setValorResgate('');
      setNotas('');
      loadHistorico();
    } catch (err: any) {
      setError(err.message || 'Erro ao resgatar voucher');
    } finally {
      setRedeeming(false);
    }
  };

  const fmt = (n: number | null | undefined) =>
    n == null ? '-' : `${Number(n).toLocaleString('pt-CV')} ECV`;

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Validar / Resgatar Voucher</h1>
          <p className="text-gray-600">Valida o código do cliente e regista o resgate.</p>
        </div>
        <Link
          to="/portal/vouchers"
          className="text-sm text-pagali-primary hover:text-pagali-primary/80"
        >
          ← Voltar aos vouchers
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
          {error}
        </div>
      )}

      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
          {successMsg}
        </div>
      )}

      {/* Input card */}
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={(e) => handleValidar(e)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Código do voucher ou dados do QR
            </label>
            <textarea
              rows={3}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ex: PGV-M1A2B3-XYZ123  ou  {&quot;type&quot;:&quot;pagali_voucher&quot;,...}"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-pagali-primary"
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-1">
              Dica: usa a câmera, cola o conteúdo do QR exportado, ou introduz o código único manualmente.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={startCamera}
              className="px-4 py-2 bg-white border border-pagali-primary text-pagali-primary rounded-lg hover:bg-pagali-primary/5 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Usar câmera
            </button>
            <button
              type="submit"
              disabled={checking || !input.trim()}
              className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80 disabled:opacity-60"
            >
              {checking ? 'A validar...' : 'Validar'}
            </button>
            {(voucher || input) && (
              <button
                type="button"
                onClick={reset}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Limpar
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Camera scanner modal */}
      {cameraOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold">Ler QR do voucher</h3>
              <button
                onClick={async () => {
                  await stopCamera();
                  setCameraOpen(false);
                }}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <div id={scannerElemId} className="w-full" />
              {cameraError && (
                <p className="text-sm text-red-600 mt-2">{cameraError}</p>
              )}
              <p className="text-xs text-gray-500 mt-3 text-center">
                Aponta a câmera ao QR do voucher. A validação inicia automaticamente.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Voucher card */}
      {voucher && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div
            className={`px-6 py-3 ${
              voucher.valido ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}
          >
            <p className="font-semibold">
              {voucher.valido ? '✓ Voucher válido' : '✗ Voucher não utilizável'}
            </p>
            <p className="text-sm">{voucher.mensagem}</p>
          </div>

          <div className="p-6 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Voucher:</span>{' '}
                <span className="font-medium">{voucher.titulo}</span>
              </div>
              <div>
                <span className="text-gray-500">Código:</span>{' '}
                <span className="font-mono">{voucher.codigo_unico}</span>
              </div>
              <div>
                <span className="text-gray-500">Tipo:</span>{' '}
                <span className="font-medium">{voucher.tipo_categoria}</span>
              </div>
              <div>
                <span className="text-gray-500">Beneficiário:</span>{' '}
                {voucher.beneficiario_nome || '-'}
              </div>
              {voucher.tipo_categoria === 'valor' ? (
                <>
                  <div>
                    <span className="text-gray-500">Valor facial:</span>{' '}
                    <span className="font-medium">{fmt(voucher.valor)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Saldo restante:</span>{' '}
                    <span className="font-semibold text-pagali-primary">
                      {fmt(voucher.valor_restante ?? voucher.valor)}
                    </span>
                  </div>
                </>
              ) : (
                <div>
                  <span className="text-gray-500">Desconto:</span>{' '}
                  <span className="font-medium">{voucher.percentagem_desconto}% OFF</span>
                </div>
              )}
              <div>
                <span className="text-gray-500">Validade:</span>{' '}
                {voucher.data_validade
                  ? fmtParede(voucher.data_validade, { day: '2-digit', month: '2-digit', year: 'numeric' }, 'pt-PT')
                  : 'Sem validade'}
              </div>
            </div>

            {voucher.valido && (
              <div className="pt-4 border-t space-y-3">
                {voucher.tipo_categoria === 'valor' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor a resgatar (ECV) *
                    </label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      max={voucher.valor_restante ?? undefined}
                      value={valorResgate}
                      onChange={(e) => setValorResgate(e.target.value)}
                      className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notas (opcional)
                  </label>
                  <input
                    type="text"
                    maxLength={500}
                    value={notas}
                    onChange={(e) => setNotas(e.target.value)}
                    placeholder="Ex: Refeição mesa 5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                </div>
                <button
                  onClick={handleResgatar}
                  disabled={redeeming}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60"
                >
                  {redeeming ? 'A resgatar...' : 'Confirmar Resgate'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Redemption history */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Resgates recentes</h2>
        </div>
        {historico.length === 0 ? (
          <p className="p-6 text-center text-gray-500">Ainda não há resgates.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Voucher</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor / Desconto</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Operador</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {historico.map((h) => (
                  <tr key={h.id}>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {new Date(h.created_at).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}
                    </td>
                    <td className="px-4 py-2 text-sm">{h.voucher_titulo}</td>
                    <td className="px-4 py-2 text-sm font-mono text-xs">{h.codigo_unico}</td>
                    <td className="px-4 py-2 text-sm">
                      {h.valor_resgatado != null
                        ? fmt(h.valor_resgatado)
                        : h.desconto_aplicado != null
                        ? `${h.desconto_aplicado}%`
                        : '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">{h.operador_nome || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalVoucherScanner;
